import React, { useEffect } from "react";
import * as Styled from "./Reorder.styled";
import { createTheme, ThemeProvider } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import DragRows from "../../../../../assets/images/icons/icon-drag-rows.svg";
import DefaultThumbnailImage from "../../../../../assets/images/default_thumbnail.jpeg";
import { FullScreenModal, HeaderLogo, IPlaylistMediaItem, IReorderProps } from "../../../../../libs";
import AdPreviewImage from "../../../../../assets/images/layout/AdPreviewImage_01.png";
import SearchIcon from "../../../../../assets/images/icons/search.svg";
import { Image } from "react-bootstrap";
import { toast } from "react-toastify";

export const Reorder: React.FC<IReorderProps> = ({ selectedVideos, mediaItems, setmediaItems, show, onHide }) => {
    const [originalData, setOriginalData] = React.useState<IPlaylistMediaItem[]>([]);
    const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
    const [inputValue, setInputValue] = React.useState<string>("");
    const [searchQuery, setSearchQuery] = React.useState<string>("");

    const columns = React.useMemo<MRT_ColumnDef<IPlaylistMediaItem>[]>(() => {
        return [
            {
                header: "#",
                id: "rowNumber",
                Cell: ({ row }) => {
                    const currentIndex = row.original.numb;

                    const isValidIndex = (val) => {
                        const num = parseInt(val);
                        return !isNaN(num) && num > 0 && num <= data.length;
                    };

                    const handleInputBlurOrEnter = (fromEnter = false) => {
                        const newIndex = parseInt(inputValue) - 1;

                        if (!inputValue.trim()) {
                            setEditingIndex(null);
                            return;
                        }

                        if (isNaN(newIndex) || newIndex < 0 || newIndex >= data.length) {
                            if (fromEnter) {
                                toast.error(`Please enter a number between 1 and ${data.length}`);
                            }
                            return;
                        }

                        if (newIndex !== currentIndex) {
                            const updatedData = [...data];
                            const [movedItem] = updatedData.splice(currentIndex, 1);
                            updatedData.splice(newIndex, 0, movedItem);

                            const reindexedData = updatedData.map((item, index) => ({
                                ...item,
                                numb: index,
                            }));

                            setData(reindexedData);
                        }

                        setEditingIndex(null);
                    };

                    return editingIndex === currentIndex ? (
                        <Styled.StyledNumberInput
                            type="number"
                            value={inputValue}
                            isValid={isValidIndex(inputValue) || !inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onBlur={() => handleInputBlurOrEnter(false)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleInputBlurOrEnter(true);
                            }}
                            autoFocus
                        />
                    ) : (
                        <Styled.ClickableIndex
                            onClick={() => {
                                setEditingIndex(currentIndex);
                                setInputValue((currentIndex + 1).toString());
                            }}
                        >
                            {currentIndex + 1}
                        </Styled.ClickableIndex>
                    );
                },
                size: 20,
            },
            {
                header: "Thumbnail",
                Cell: ({ renderedCellValue, row }) =>
                    <Styled.TitleCellWrapper>
                        <img
                            src={
                                row.original.thumbnail
                                    ? row.original.thumbnail
                                    : DefaultThumbnailImage
                            }
                            alt="Title"
                        />
                        <div>{renderedCellValue}</div>
                    </Styled.TitleCellWrapper>
            },
            {
                header: "Title",
                accessorKey: "title",
                Cell: ({ renderedCellValue }) => (
                    <Styled.TitleCellWrapper>
                        <div>{renderedCellValue}</div>
                    </Styled.TitleCellWrapper>
                ),
            },
            {
                header: "Created Date",
                accessorKey: "publishedDateFormatted",
                Cell: ({ cell }) => (
                    <span style={{ fontSize: "14px", color: "#ccc" }}>
                        {cell.getValue() as string || "N/A"}
                    </span>
                ),
            },
        ];
    }, [editingIndex, inputValue]);


    const [data, setData] = React.useState<IPlaylistMediaItem[]>([]);

    useEffect(() => {
        if (show) {
            setData(mediaItems);
            setOriginalData(mediaItems);
        }
        else {
            setSearchQuery("");
        }
    }, [show]);

    const handleCancel = () => {
        setData(originalData);
        onHide();
    };

    React.useEffect(() => {
        setData(mediaItems)
    }, [selectedVideos, mediaItems]);

    const SaveClick = () => {
        setmediaItems(data)
        onHide()
    }

    const table = useMaterialReactTable({
        autoResetPageIndex: false,
        columns,
        data: data.filter(item =>
            item.title?.toLowerCase().includes(searchQuery.toLowerCase())
        ),
        enableRowOrdering: true,
        enableSorting: true,
        enablePagination: true,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableFilters: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        manualPagination: false,
        paginationDisplayMode: 'pages',
        renderTopToolbar: '',
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 10,
            },
        },
        muiPaginationProps: {
            showRowsPerPage: true,
            showFirstButton: true,
            showLastButton: true,
        },
        positionPagination: "bottom",
        muiRowDragHandleProps: ({ table }) => ({
            onDragEnd: () => {
                const { draggingRow, hoveredRow } = table.getState();

                const oldIndex = draggingRow?.index;
                const newIndex = hoveredRow?.index;

                // Make sure both indices are numbers
                if (typeof oldIndex === 'number' && typeof newIndex === 'number') {
                    const updatedData = [...data];
                    const [movedItem] = updatedData.splice(oldIndex, 1);
                    updatedData.splice(newIndex, 0, movedItem);
                    setData(updatedData);
                }
            },
        }),


        icons: {
            DragHandleIcon: () => {
                return (
                    <img
                        src={DragRows}
                        alt="Drag Rows"
                        style={{ zIndex: "-1" }}
                    />
                );
            },
        },
        mrtTheme: {
            baseBackgroundColor: "#0F1013",
            draggingBorderColor: "#b6f84d",
        },
    });

    const theme = createTheme({
        palette: {
            mode: "dark",
        },
    });

    return (
        <FullScreenModal show={show} onHide={onHide}
            header={
                <Styled.ModalHeader closeButton closeVariant="white">
                    <HeaderLogo />
                </Styled.ModalHeader>
            }
            body={
                <Styled.OrderSelectionWrapper>
                    <Styled.LeftSection>
                        <ThemeProvider theme={theme}>
                            <Styled.OrderSelectionRow>
                                <Styled.OrderSelectionCol>
                                    <Styled.OrderSelectionHeader>
                                        <Styled.OrderSelectionHeaderText>
                                            Select video order
                                        </Styled.OrderSelectionHeaderText>
                                        <Styled.OrderSelectionHeaderActions>
                                            <Styled.DashboardSearchWrapper>
                                                <Styled.SearchBarWrapper>
                                                    <Styled.SearchIcon src={SearchIcon} alt="Search" />
                                                    <Styled.SearchInput
                                                        name="search"
                                                        type="text"
                                                        placeholder="Search by title"
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                    />
                                                </Styled.SearchBarWrapper>
                                            </Styled.DashboardSearchWrapper>
                                            <Styled.CustomButton variant="outline-secondary" onClick={() => { handleCancel() }}>
                                                Cancel
                                            </Styled.CustomButton>
                                            <Styled.SaveButton variant="primary" onClick={() => { SaveClick() }}>Save</Styled.SaveButton>
                                        </Styled.OrderSelectionHeaderActions>
                                    </Styled.OrderSelectionHeader>
                                </Styled.OrderSelectionCol>
                            </Styled.OrderSelectionRow>
                            <Styled.OrderSelectionRow className="mt-4">
                                <Styled.OrderSelectionCol >
                                    {originalData.length === 0 ? (
                                        <Styled.NoResultsText>No results found.</Styled.NoResultsText>
                                    ) : (
                                        <MaterialReactTable table={table} />
                                    )}
                                </Styled.OrderSelectionCol>
                            </Styled.OrderSelectionRow>

                        </ThemeProvider>
                    </Styled.LeftSection>

                    <Styled.RightSection>
                        <Styled.PreviewWrapper>
                            <Styled.PreviewHeader>Preview</Styled.PreviewHeader>
                            <Image src={AdPreviewImage} width={500} />
                        </Styled.PreviewWrapper>
                    </Styled.RightSection>
                </Styled.OrderSelectionWrapper>
            }
        />
    );
};

